// Core
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { isAuthenticated } from 'utils/helpers';

/**
 * Route guard for the pages that only mean anything with a session behind them
 * — the account screens the Header itself hides from signed-out readers.
 *
 * Guarding at the route rather than inside each page matters: the page never
 * mounts, so its effects never fire and no account endpoint is called on behalf
 * of someone who has not signed in. It also removes the flash of protected
 * content that an in-page redirect always shows.
 *
 * `replace` keeps the guarded path out of the history stack, so Back from
 * /home does not bounce the reader straight back into the redirect.
 *
 * Usable two ways: as a layout route wrapping several children (no `children`
 * prop — it renders the matched child through `Outlet`), or around a single
 * element. The layout form is what the router uses, so the guard is declared
 * once for the whole account section instead of per route.
 */
const RequireAuth = ({ children, redirectTo }) => {
  const { userDetails } = useSelector((store) => store.commonData);
  const location = useLocation();

  if (!isAuthenticated(userDetails))
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;

  return children === undefined ? <Outlet /> : children;
};

RequireAuth.defaultProps = {
  redirectTo: '/home'
};

RequireAuth.propTypes = {
  children: PropTypes.node,
  redirectTo: PropTypes.string
};

export default RequireAuth;
